import React, { useState, useEffect, useMemo } from "react";
import { FaTimes, FaSearch, FaPlus, FaUndo } from "react-icons/fa";
import { useLazyGetDeviceDetailQuery } from "../../store/storageRoles/devicesSharedApi";
import {
  useGiveDeviceToMachineWorkerMutation,
  useLazyGetMachineWorkerDevicesQuery,
} from "../../store/storageRoles/usersApi";
import { useDebounce } from "../../Hooks/useDebounce";
import { deviceActivityStateLabel } from "../../utils/deviceActivityLabels";

const SEARCH_MIN = 2;
const SEARCH_LIMIT = 20;

/** getDeviceDetail satırı → pencerenin ortak makine biçimi */
const fromSearch = (d) => ({
  _id: d._id,
  serialNo: d.serialNo,
  deviceName: d.deviceInfo?.deviceName || d.deviceName || "",
  customName: d.customName || "",
  firmName: d.firm?.firmName || "",
  isActive: !!d.isActive,
});

const Spinner = () => (
  <div className="flex justify-center py-6">
    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const DeviceRow = ({ device, badge, action }) => (
  <div className="flex items-center gap-3 px-4 py-3">
    <div className="flex-1 min-w-0">
      <div className="font-medium text-gray-900 truncate">
        {device.customName || device.deviceName || "İsimsiz Cihaz"}
        {device.customName && device.deviceName ? (
          <span className="ml-1.5 text-xs font-normal text-gray-500">{device.deviceName}</span>
        ) : null}
      </div>
      <div className="text-sm text-gray-500 truncate">
        {device.serialNo}
        {device.firmName ? ` · ${device.firmName}` : ""}
      </div>
    </div>
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
        device.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
      }`}
    >
      {deviceActivityStateLabel(device.isActive)}
    </span>
    {badge}
    {action}
  </div>
);

/**
 * Makine görevlisine makine atama (#323). Üstte atanmış makineler (çıkarılabilir),
 * altta seri no ile sunucudan arama. Kaydet son listeyi gönderir; sunucu listeyi
 * aynen kaydeder (çıkarılan makine görevliden düşer).
 */
const DeviceAssignmentModal = ({ open, onClose, token, machineWorkerId, selectedUser, onSuccess }) => {
  const [list, setList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedTerm = useDebounce(searchTerm.trim(), 300);

  const [loadAssigned, { data: assignedData, isFetching: assignedLoading, error: assignedError }] =
    useLazyGetMachineWorkerDevicesQuery();
  const [searchDevices, { data: searchData, isFetching: searching, error: searchError }] =
    useLazyGetDeviceDetailQuery();
  const [giveDeviceToMachineWorker, { isLoading: isSaving, error: saveError, reset: resetSave }] =
    useGiveDeviceToMachineWorkerMutation();

  const assigned = useMemo(() => assignedData?.data?.devices || [], [assignedData]);

  useEffect(() => {
    if (open && token && machineWorkerId) loadAssigned({ token, machineWorkerId });
    if (!open) {
      setSearchTerm("");
      resetSave();
    }
  }, [open, token, machineWorkerId, loadAssigned, resetSave]);

  // Sunucudaki liste gelince çalışma listesi ondan başlar
  useEffect(() => {
    if (open) setList(assigned);
  }, [assigned, open]);

  useEffect(() => {
    if (open && token && debouncedTerm.length >= SEARCH_MIN) {
      searchDevices({ token, serialNo: debouncedTerm, limit: SEARCH_LIMIT, page: 1 });
    }
  }, [open, token, debouncedTerm, searchDevices]);

  const listIds = useMemo(() => new Set(list.map((d) => d._id)), [list]);
  const assignedIds = useMemo(() => new Set(assigned.map((d) => d._id)), [assigned]);
  const added = list.filter((d) => !assignedIds.has(d._id));
  const removed = assigned.filter((d) => !listIds.has(d._id));
  const changed = added.length > 0 || removed.length > 0;

  const results =
    debouncedTerm.length >= SEARCH_MIN ? (searchData?.devices || []).map(fromSearch) : [];

  const add = (device) => setList((prev) => (prev.some((d) => d._id === device._id) ? prev : [...prev, device]));
  const remove = (id) => setList((prev) => prev.filter((d) => d._id !== id));

  const handleSave = async () => {
    if (!changed || isSaving) return;
    try {
      await giveDeviceToMachineWorker({ token, machineWorkerId, deviceIds: list.map((d) => d._id) }).unwrap();
      onSuccess?.();
    } catch {
      // Hata pencerede gösterilir, kullanıcı tekrar deneyebilir
    }
  };

  if (!open) return null;

  const workerName = [selectedUser?.name, selectedUser?.lastName].filter(Boolean).join(" ");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Cihaz Atama</h2>
            {workerName ? <p className="text-sm text-gray-500 mt-0.5">{workerName}</p> : null}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" disabled={isSaving}>
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
          {/* Atanmış makineler */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Atanmış cihazlar ({list.length})
            </h3>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
              {assignedLoading ? (
                <Spinner />
              ) : assignedError ? (
                <p className="text-red-600 text-sm p-4">
                  {assignedError?.data?.message || "Atanmış cihazlar yüklenemedi."}
                </p>
              ) : list.length === 0 && removed.length === 0 ? (
                <p className="text-gray-500 text-sm p-4">Atanmış cihaz yok. Aşağıdan seri no ile arayıp ekleyin.</p>
              ) : (
                <>
                  {list.map((d) => (
                    <DeviceRow
                      key={d._id}
                      device={d}
                      badge={
                        assignedIds.has(d._id) ? null : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            Eklenecek
                          </span>
                        )
                      }
                      action={
                        <button
                          onClick={() => remove(d._id)}
                          className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded-lg"
                        >
                          Çıkar
                        </button>
                      }
                    />
                  ))}
                  {removed.map((d) => (
                    <div key={d._id} className="opacity-60 bg-gray-50">
                      <DeviceRow
                        device={d}
                        badge={
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Çıkarılacak
                          </span>
                        }
                        action={
                          <button
                            onClick={() => add(d)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg"
                          >
                            <FaUndo className="w-3 h-3" /> Geri al
                          </button>
                        }
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          </section>

          {/* Arama */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Cihaz ekle</h3>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Seri no ile ara (en az 2 karakter)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            {debouncedTerm.length >= SEARCH_MIN ? (
              <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-200">
                {searching ? (
                  <Spinner />
                ) : searchError ? (
                  <p className="text-red-600 text-sm p-4">Cihazlar aranırken hata oluştu.</p>
                ) : results.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4">Bu seri no ile cihaz bulunamadı.</p>
                ) : (
                  results.map((d) => (
                    <DeviceRow
                      key={d._id}
                      device={d}
                      action={
                        listIds.has(d._id) ? (
                          <span className="px-3 py-1.5 text-xs font-semibold text-gray-500">Listede</span>
                        ) : (
                          <button
                            onClick={() => add(d)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg"
                          >
                            <FaPlus className="w-3 h-3" /> Ekle
                          </button>
                        )
                      }
                    />
                  ))
                )}
                {(searchData?.pagination?.totalDevices || 0) > SEARCH_LIMIT ? (
                  <p className="text-xs text-gray-500 px-4 py-2">
                    İlk {SEARCH_LIMIT} sonuç gösteriliyor, aramayı daraltın.
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>

          {saveError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">
                {saveError?.data?.message || "Cihaz ataması kaydedilemedi."}
              </p>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200">
          <span className="text-sm text-gray-600">
            {changed
              ? [added.length && `${added.length} eklenecek`, removed.length && `${removed.length} çıkarılacak`]
                  .filter(Boolean)
                  .join(" · ")
              : "Değişiklik yok"}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              disabled={isSaving}
            >
              İptal
            </button>
            <button
              onClick={handleSave}
              disabled={!changed || isSaving}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceAssignmentModal;
